import React, { lazy, Suspense } from 'react';
import { Navigate, Route, useLocation } from 'react-router-dom';
// Imported from the module, not the `features/auth` barrel: the barrel re-exports
// a `.jsx` file, which jest's transform does not cover.
import { ProtectedRoute } from '../auth/components/ProtectedRoute';
import { RequireActiveAccount } from '../../router/guards';
import { featureFlags } from '../../config/featureFlags';
import { LEARNING_ROUTE_SEGMENT, MCP_SEGMENT, NEWS_SEGMENT } from './constants';
// By the component path: the skeleton imports nothing of the feature but its own copy.
import { LearningSkeleton } from './components/LearningSkeleton/LearningSkeleton';
import { learningSkeletonKind } from './components/LearningSkeleton/learningSkeletonKind';

// Page modules, not the feature barrel: importing this file from AppRouter must not pull the
// feature into the main bundle. The gate's <Suspense> covers the layout with the screen's own
// skeleton, and the layout's own <Suspense> around its <Outlet/> covers the pages.
const LearningLayout = lazy(() =>
  import('./pages/LearningLayout').then((m) => ({ default: m.LearningLayout })),
);
const LearningHomePage = lazy(() =>
  import('./pages/LearningHomePage').then((m) => ({ default: m.LearningHomePage })),
);
const LearningStagePage = lazy(() =>
  import('./pages/LearningStagePage').then((m) => ({ default: m.LearningStagePage })),
);
const LearningNewsPage = lazy(() =>
  import('./pages/LearningNewsPage').then((m) => ({ default: m.LearningNewsPage })),
);
const LearningMcpPage = lazy(() =>
  import('./pages/LearningMcpPage').then((m) => ({ default: m.LearningMcpPage })),
);

/**
 * Behind REACT_APP_FEATURE_LEARNING while it rolls out. Every signed-in role may open it: what a
 * reader can try is bounded by their own session (the API answers with their permissions), and
 * the links to the docs hub only show for the roles AppRouter lets into /docs.
 */
const LearningGate: React.FC = () => {
  const { pathname } = useLocation();
  // Off: nothing of the section draws, not even its loading shape.
  if (!featureFlags.learning) return <Navigate to=".." replace />;
  return (
    <ProtectedRoute>
      <RequireActiveAccount>
        <Suspense fallback={<LearningSkeleton kind={learningSkeletonKind(pathname) ?? 'home'} />}>
          <LearningLayout />
        </Suspense>
      </RequireActiveAccount>
    </ProtectedRoute>
  );
};

/**
 * Route subtree of Silia Learning, mounted by AppRouter under the account
 * (`accounts/:accountId/learning`). A `Route` element and not a component: `<Routes>` only
 * accepts `Route` children. The fixed pages come before `:stageId`, which takes the rest.
 */
export const learningRoutes = (
  <Route path={LEARNING_ROUTE_SEGMENT} element={<LearningGate />}>
    <Route index element={<LearningHomePage />} />
    <Route path={NEWS_SEGMENT} element={<LearningNewsPage />} />
    <Route path={MCP_SEGMENT} element={<LearningMcpPage />} />
    <Route path=":stageId" element={<LearningStagePage />} />
  </Route>
);
